/**
 * Shared chart tooltip — one styled card used by EVERY chart (donuts,
 * pies, bar lists, column charts, sparklines) so the hover experience is
 * identical app-wide. A colour dot + label + value, tracking the cursor.
 * `pointer-events-none` so it never steals the hover it's reporting on.
 *
 * Pairs with `useChartHover` — spread its `hover` payload in:
 *   {hover && <ChartTooltip {...hover} bounds={wrapperWidthPx} />}
 *
 * Payload fields: `x`, `y` (pointer coords relative to the wrapper),
 * `color` (optional swatch), `label`, `value` (string/number, optional),
 * `hint` (optional second muted line).
 */
export function ChartTooltip({
  x,
  y,
  clientX,
  clientY,
  color,
  label,
  value,
  hint,
  bounds,
  w,
}) {
  if (x == null || y == null) return null

  const card = (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2.5 py-1.5 shadow-lg">
      <div className="flex items-center gap-2 whitespace-nowrap">
        {color ? (
          <span
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
        ) : null}
        <span className="text-xs font-medium text-[color:var(--color-ink-strong)]">
          {label}
        </span>
        {value != null ? (
          <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)]">
            {value}
          </span>
        ) : null}
      </div>
      {hint ? (
        <p className="mt-0.5 text-[11px] text-[color:var(--color-ink-muted)] whitespace-nowrap">
          {hint}
        </p>
      ) : null}
    </div>
  )

  // Preferred path — viewport-fixed positioning. Escapes any parent
  // overflow/clipping context (narrow table columns, `overflow-hidden`
  // wrappers, etc.) and flips its origin left/center/right based on
  // where the pointer sits so the card never spills off the viewport
  // — including at Large font sizes where the card is wider.
  if (clientX != null && clientY != null) {
    const vw =
      typeof window !== 'undefined' && window.innerWidth
        ? window.innerWidth
        : 1200
    const MARGIN = 12
    // Zone the pointer into left / centre / right thirds of the
    // viewport and pick the transform origin accordingly. No
    // measurement of the card is needed — the CSS transform pulls the
    // card to the correct side of the pointer, so it stays on-screen
    // whatever its size.
    let translate = 'translate(-50%, -100%)'
    if (clientX < 160) translate = 'translate(0, -100%)'
    else if (clientX > vw - 160) translate = 'translate(-100%, -100%)'
    return (
      <div
        className="pointer-events-none fixed z-[100]"
        style={{
          left: clientX,
          top: Math.max(clientY - 10, MARGIN),
          transform: translate,
          maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
        }}
      >
        {card}
      </div>
    )
  }

  // Fallback path — wrapper-relative absolute positioning. Kept for any
  // caller that doesn't route through `useChartHover` (or a future one
  // that passes wrapper-relative coords directly). Clamps to the chart
  // wrapper's own width so the card never spills past the edge of a
  // wide bar/column chart.
  const clamp = bounds ?? w
  const left = clamp != null ? Math.min(Math.max(x, 4), clamp - 4) : x
  return (
    <div
      className="pointer-events-none absolute z-20"
      style={{
        left,
        top: Math.max(y - 10, 0),
        transform: 'translate(-50%, -100%)',
      }}
    >
      {card}
    </div>
  )
}
